import { useRef, useEffect, useState, useMemo } from 'react'

export default function ReplayEventLog({ events, currentTime, startTime }) {
  const containerRef = useRef(null)
  const activeRef = useRef(null)
  const [activeTab, setActiveTab] = useState('events') // 'events' or 'observables'
  const [activeSubTab, setActiveSubTab] = useState('before') // 'before' or 'after'

  // Auto-scroll to active event
  useEffect(() => {
    if (activeTab === 'events' && activeRef.current && containerRef.current) {
      activeRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [currentTime, activeTab])

  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000)
    const m = Math.floor(totalSeconds / 60)
    const s = totalSeconds % 60
    return `${m}:${s < 10 ? '0' : ''}${s}`
  }

  const formatDuration = (ms) => {
    if (ms < 1000) return `${ms}ms`
    const seconds = Math.floor(ms / 1000)
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }

  // Find the current active event index
  const getActiveIndex = () => {
    for (let i = events.length - 1; i >= 0; i--) {
      const eventTime = events[i].timestamp - startTime
      if (eventTime <= currentTime) {
        return i
      }
    }
    return -1
  }

  const activeIndex = getActiveIndex()

  // Find first CHAT_QUERY index for observables split
  const firstQueryIndex = events.findIndex(e => e.type === 'CHAT_QUERY')

  // Split events for observables tab
  const beforeFirstQuery = firstQueryIndex === -1 
    ? events 
    : events.slice(0, firstQueryIndex)
  
  const afterFirstQuery = firstQueryIndex === -1 
    ? [] 
    : events.slice(firstQueryIndex)

  // Calculate stats for observables
  const observableStats = useMemo(() => {
    const calculateStats = (eventList) => {
      if (eventList.length === 0) {
        return {
          regionTimes: {
            CODE_EDITOR: 0,
            CHAT_WINDOW: 0,
            TERMINAL: 0,
            TASK_DESCRIPTION: 0,
            TEST_CASES: 0,
            BACKGROUND: 0,
          },
          timeSinceLast: {
            codeEdit: null,
            terminalRun: null,
            error: null,
            chat: null,
          },
          idleStats: {
            latestIdle: null,
            longestIdle: null,
          },
          frequency: {
            codeEdit: { count: 0, perMin: '0.0' },
            codeSelect: { count: 0, perMin: '0.0' },
            codeCopy: { count: 0, perMin: '0.0' },
            codePaste: { count: 0, perMin: '0.0' },
            codeDelete: { count: 0, perMin: '0.0' },
            codeUndo: { count: 0, perMin: '0.0' },
            terminalRun: { count: 0, perMin: '0.0' },
            terminalError: { count: 0, perMin: '0.0' },
            terminalOutput: { count: 0, perMin: '0.0' },
            terminalSelect: { count: 0, perMin: '0.0' },
            terminalCopy: { count: 0, perMin: '0.0' },
            chatInputType: { count: 0, perMin: '0.0' },
            chatInputSelect: { count: 0, perMin: '0.0' },
            chatInputCopy: { count: 0, perMin: '0.0' },
            chatInputPaste: { count: 0, perMin: '0.0' },
            chatQuery: { count: 0, perMin: '0.0' },
            chatHistorySelect: { count: 0, perMin: '0.0' },
            chatHistoryCopy: { count: 0, perMin: '0.0' },
            mouseMove: { count: 0, perMin: '0.0' },
            mouseClick: { count: 0, perMin: '0.0' },
          },
          terminalMetrics: {
            meanTimeBetweenRuns: null,
            errorToEditLatency: null,
            errorReadingTime: null,
          },
          codeMetrics: {
            netCodeGrowth: 0,
            deletionToTypeRatio: null,
          },
          chatMetrics: {
            timeToHelp: null,
            chatToCodeLatency: null,
            avgQueryLength: null,
          },
          tabVisibility: {
            hiddenTime: 0,
            hiddenCount: 0,
          },
          taskTestInteractions: {
            taskSelect: 0,
            taskCopy: 0,
            testSelect: 0,
            testCopy: 0,
          },
          testResults: {
            passCount: 0,
            failCount: 0,
            lastPassedCount: null,
            lastTotalTests: null,
          },
          errorPatterns: {
            consecutiveErrors: 0,
            maxConsecutiveErrors: 0,
          },
          periodDuration: 0,
        }
      }

      const periodStart = eventList[0].timestamp
      const periodEnd = eventList[eventList.length - 1].timestamp
      const periodDuration = periodEnd - periodStart

      // Region times calculation
      const regionTimes = {
        CODE_EDITOR: 0,
        CHAT_WINDOW: 0,
        TERMINAL: 0,
        TASK_DESCRIPTION: 0,
        TEST_CASES: 0,
        BACKGROUND: 0,
      }

      const mouseEvents = eventList.filter(e => 
        (e.type === 'MOUSE_MOVE' || e.type === 'MOUSE_CLICK') && e.payload?.region
      )

      for (let i = 0; i < mouseEvents.length - 1; i++) {
        const current = mouseEvents[i]
        const next = mouseEvents[i + 1]
        const region = current.payload.region
        const duration = next.timestamp - current.timestamp

        if (duration > 0 && duration < 30000 && regionTimes.hasOwnProperty(region)) {
          regionTimes[region] += duration
        }
      }

      // Time since last events
      const lastCodeEdit = [...eventList].reverse().find(e => e.type?.startsWith('CODE_'))
      const lastTerminalRun = [...eventList].reverse().find(e => e.type === 'TERMINAL_RUN')
      const lastError = [...eventList].reverse().find(e => 
        e.type === 'TERMINAL_ERROR' || e.type === 'CHAT_ERROR'
      )
      const lastChat = [...eventList].reverse().find(e => 
        e.type === 'CHAT_QUERY' || e.type === 'CHAT_RESPONSE'
      )

      const timeSinceLast = {
        codeEdit: lastCodeEdit ? periodEnd - lastCodeEdit.timestamp : null,
        terminalRun: lastTerminalRun ? periodEnd - lastTerminalRun.timestamp : null,
        error: lastError ? periodEnd - lastError.timestamp : null,
        chat: lastChat ? periodEnd - lastChat.timestamp : null,
      }

      // Idle stats (gaps > 2 seconds between any events)
      const IDLE_THRESHOLD = 2000 // 2 seconds
      let latestIdle = null
      let longestIdle = null

      for (let i = 0; i < eventList.length - 1; i++) {
        const gap = eventList[i + 1].timestamp - eventList[i].timestamp
        if (gap >= IDLE_THRESHOLD) {
          latestIdle = gap
          if (longestIdle === null || gap > longestIdle) {
            longestIdle = gap
          }
        }
      }

      // Frequency counts
      const codeEditCount = eventList.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT').length
      const codeSelectCount = eventList.filter(e => e.type === 'CODE_SELECT').length
      const codeCopyCount = eventList.filter(e => e.type === 'CODE_COPY').length
      const codePasteCount = eventList.filter(e => e.type === 'CODE_PASTE').length
      const codeDeleteCount = eventList.filter(e => e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION' || e.type === 'CODE_CUT').length
      const codeUndoCount = eventList.filter(e => e.type === 'CODE_UNDO' || e.type === 'CODE_REDO').length
      const terminalRunCount = eventList.filter(e => e.type === 'TERMINAL_RUN').length
      const terminalErrorCount = eventList.filter(e => e.type === 'TERMINAL_ERROR').length
      const terminalOutputCount = eventList.filter(e => e.type === 'TERMINAL_OUTPUT').length
      const terminalSelectCount = eventList.filter(e => e.type === 'TERMINAL_SELECT').length
      const terminalCopyCount = eventList.filter(e => e.type === 'TERMINAL_COPY').length
      const chatTypeCount = eventList.filter(e => e.type === 'CHAT_TYPE').length
      const chatInputSelectCount = eventList.filter(e => e.type === 'CHAT_SELECT_INPUT').length
      const chatInputCopyCount = eventList.filter(e => e.type === 'CHAT_COPY_INPUT').length
      const chatInputPasteCount = eventList.filter(e => e.type === 'CHAT_PASTE').length
      const chatQueryCount = eventList.filter(e => e.type === 'CHAT_QUERY').length
      const chatHistorySelectCount = eventList.filter(e => e.type === 'CHAT_SELECT_HISTORY').length
      const chatHistoryCopyCount = eventList.filter(e => e.type === 'CHAT_COPY_HISTORY').length
      const mouseMoveCount = eventList.filter(e => e.type === 'MOUSE_MOVE').length
      const mouseClickCount = eventList.filter(e => e.type === 'MOUSE_CLICK').length

      // Calculate per-minute frequency
      const minutes = periodDuration / 60000 || 1 // Avoid division by zero

      const frequency = {
        codeEdit: { count: codeEditCount, perMin: (codeEditCount / minutes).toFixed(1) },
        codeSelect: { count: codeSelectCount, perMin: (codeSelectCount / minutes).toFixed(1) },
        codeCopy: { count: codeCopyCount, perMin: (codeCopyCount / minutes).toFixed(1) },
        codePaste: { count: codePasteCount, perMin: (codePasteCount / minutes).toFixed(1) },
        codeDelete: { count: codeDeleteCount, perMin: (codeDeleteCount / minutes).toFixed(1) },
        codeUndo: { count: codeUndoCount, perMin: (codeUndoCount / minutes).toFixed(1) },
        terminalRun: { count: terminalRunCount, perMin: (terminalRunCount / minutes).toFixed(1) },
        terminalError: { count: terminalErrorCount, perMin: (terminalErrorCount / minutes).toFixed(1) },
        terminalOutput: { count: terminalOutputCount, perMin: (terminalOutputCount / minutes).toFixed(1) },
        terminalSelect: { count: terminalSelectCount, perMin: (terminalSelectCount / minutes).toFixed(1) },
        terminalCopy: { count: terminalCopyCount, perMin: (terminalCopyCount / minutes).toFixed(1) },
        chatInputType: { count: chatTypeCount, perMin: (chatTypeCount / minutes).toFixed(1) },
        chatInputSelect: { count: chatInputSelectCount, perMin: (chatInputSelectCount / minutes).toFixed(1) },
        chatInputCopy: { count: chatInputCopyCount, perMin: (chatInputCopyCount / minutes).toFixed(1) },
        chatInputPaste: { count: chatInputPasteCount, perMin: (chatInputPasteCount / minutes).toFixed(1) },
        chatQuery: { count: chatQueryCount, perMin: (chatQueryCount / minutes).toFixed(1) },
        chatHistorySelect: { count: chatHistorySelectCount, perMin: (chatHistorySelectCount / minutes).toFixed(1) },
        chatHistoryCopy: { count: chatHistoryCopyCount, perMin: (chatHistoryCopyCount / minutes).toFixed(1) },
        mouseMove: { count: mouseMoveCount, perMin: (mouseMoveCount / minutes).toFixed(1) },
        mouseClick: { count: mouseClickCount, perMin: (mouseClickCount / minutes).toFixed(1) },
      }

      // Terminal Metrics
      const terminalRuns = eventList.filter(e => e.type === 'TERMINAL_RUN')
      let meanTimeBetweenRuns = null
      if (terminalRuns.length > 1) {
        let totalGap = 0
        for (let i = 1; i < terminalRuns.length; i++) {
          totalGap += terminalRuns[i].timestamp - terminalRuns[i - 1].timestamp
        }
        meanTimeBetweenRuns = totalGap / (terminalRuns.length - 1)
      }

      // Error to edit latency - time from error to next code edit
      const errorToEditLatencies = []
      const terminalErrors = eventList.filter(e => e.type === 'TERMINAL_ERROR')
      for (const error of terminalErrors) {
        const nextCodeEdit = eventList.find(e => 
          e.timestamp > error.timestamp && e.type?.startsWith('CODE_')
        )
        if (nextCodeEdit) {
          errorToEditLatencies.push(nextCodeEdit.timestamp - error.timestamp)
        }
      }
      const errorToEditLatency = errorToEditLatencies.length > 0
        ? errorToEditLatencies.reduce((a, b) => a + b, 0) / errorToEditLatencies.length
        : null

      // Error reading time - time from error to any next action (code, chat, or terminal)
      const errorReadingTimes = []
      for (const error of terminalErrors) {
        const nextAction = eventList.find(e => 
          e.timestamp > error.timestamp && 
          (e.type?.startsWith('CODE_') || e.type === 'CHAT_QUERY' || e.type === 'TERMINAL_RUN')
        )
        if (nextAction) {
          errorReadingTimes.push(nextAction.timestamp - error.timestamp)
        }
      }
      const errorReadingTime = errorReadingTimes.length > 0
        ? errorReadingTimes.reduce((a, b) => a + b, 0) / errorReadingTimes.length
        : null

      const terminalMetrics = {
        meanTimeBetweenRuns,
        errorToEditLatency,
        errorReadingTime,
      }

      // Code Metrics
      let netCodeGrowth = 0
      const codeEvents = eventList.filter(e => e.type?.startsWith('CODE_'))
      for (const event of codeEvents) {
        if (event.payload?.changes) {
          for (const change of event.payload.changes) {
            const inserted = change.text?.length || 0
            const deleted = (change.to || 0) - (change.from || 0)
            netCodeGrowth += inserted - deleted
          }
        }
      }

      const typeEvents = eventList.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_INDENT').length
      const deleteEvents = eventList.filter(e => 
        e.type === 'CODE_DELETE' || e.type === 'CODE_DELETE_SELECTION' || e.type === 'CODE_CUT'
      ).length
      const deletionToTypeRatio = typeEvents > 0 ? (deleteEvents / typeEvents).toFixed(2) : null

      const codeMetrics = {
        netCodeGrowth,
        deletionToTypeRatio,
      }

      // Chat Metrics
      // Time to help - time from error to chat query
      const timeToHelpValues = []
      for (const error of terminalErrors) {
        const nextQuery = eventList.find(e => 
          e.timestamp > error.timestamp && e.type === 'CHAT_QUERY'
        )
        if (nextQuery) {
          timeToHelpValues.push(nextQuery.timestamp - error.timestamp)
        }
      }
      const timeToHelp = timeToHelpValues.length > 0
        ? timeToHelpValues.reduce((a, b) => a + b, 0) / timeToHelpValues.length
        : null

      // Chat to code latency - time from chat response to next code edit
      const chatToCodeLatencies = []
      const chatResponses = eventList.filter(e => e.type === 'CHAT_RESPONSE')
      for (const response of chatResponses) {
        const nextCodeEdit = eventList.find(e => 
          e.timestamp > response.timestamp && e.type?.startsWith('CODE_')
        )
        if (nextCodeEdit) {
          chatToCodeLatencies.push(nextCodeEdit.timestamp - response.timestamp)
        }
      }
      const chatToCodeLatency = chatToCodeLatencies.length > 0
        ? chatToCodeLatencies.reduce((a, b) => a + b, 0) / chatToCodeLatencies.length
        : null

      // Average query length
      const chatQueries = eventList.filter(e => e.type === 'CHAT_QUERY')
      let avgQueryLength = null
      if (chatQueries.length > 0) {
        const totalLength = chatQueries.reduce((sum, q) => {
          const text = q.payload?.text || q.payload?.message || q.payload?.content || ''
          return sum + text.length
        }, 0)
        avgQueryLength = Math.round(totalLength / chatQueries.length)
      }

      const chatMetrics = {
        timeToHelp,
        chatToCodeLatency,
        avgQueryLength,
      }

      // Tab Visibility
      const tabEvents = eventList.filter(e => e.type === 'TAB_STATE')
      let hiddenTime = 0
      let hiddenCount = 0
      let lastHiddenTimestamp = null

      for (const event of tabEvents) {
        if (event.payload?.visible === false) {
          lastHiddenTimestamp = event.timestamp
          hiddenCount++
        } else if (event.payload?.visible === true && lastHiddenTimestamp !== null) {
          hiddenTime += event.timestamp - lastHiddenTimestamp
          lastHiddenTimestamp = null
        }
      }
      // If still hidden at end of period
      if (lastHiddenTimestamp !== null) {
        hiddenTime += periodEnd - lastHiddenTimestamp
      }

      const tabVisibility = {
        hiddenTime,
        hiddenCount,
      }

      // Task/Test Interactions
      const taskSelectCount = eventList.filter(e => e.type === 'TASK_SELECT').length
      const taskCopyCount = eventList.filter(e => e.type === 'TASK_COPY').length
      const testSelectCount = eventList.filter(e => e.type === 'TEST_SELECT').length
      const testCopyCount = eventList.filter(e => e.type === 'TEST_COPY').length

      const taskTestInteractions = {
        taskSelect: taskSelectCount,
        taskCopy: taskCopyCount,
        testSelect: testSelectCount,
        testCopy: testCopyCount,
      }

      // Test Results
      const testResultEvents = eventList.filter(e => e.type === 'TEST_CASE_RESULT')
      let passCount = 0
      let failCount = 0
      let lastPassedCount = null
      let lastTotalTests = null

      for (const event of testResultEvents) {
        const passed = event.payload?.passed_count || 0
        const total = event.payload?.total_tests || 0
        const failed = total - passed

        if (passed > 0) passCount++
        if (failed > 0) failCount++

        lastPassedCount = passed
        lastTotalTests = total
      }

      const testResults = {
        passCount,
        failCount,
        lastPassedCount,
        lastTotalTests,
      }

      // Error Patterns - Consecutive errors without meaningful progress
      // "Meaningful progress" = code edit with net positive characters or test pass improvement
      let currentConsecutive = 0
      let maxConsecutiveErrors = 0
      let lastTestPassed = 0

      for (let i = 0; i < eventList.length; i++) {
        const event = eventList[i]

        if (event.type === 'TERMINAL_ERROR') {
          currentConsecutive++
          maxConsecutiveErrors = Math.max(maxConsecutiveErrors, currentConsecutive)
        } else if (event.type === 'TEST_CASE_RESULT') {
          const passed = event.payload?.passed_count || 0
          if (passed > lastTestPassed) {
            // Progress made - reset consecutive count
            currentConsecutive = 0
            lastTestPassed = passed
          }
        } else if (event.type?.startsWith('CODE_') && event.payload?.changes) {
          // Check if this is meaningful code change (net positive)
          let netChange = 0
          for (const change of event.payload.changes) {
            const inserted = change.text?.length || 0
            const deleted = (change.to || 0) - (change.from || 0)
            netChange += inserted - deleted
          }
          if (netChange > 5) {
            // At least 5 chars of net new code = meaningful progress
            currentConsecutive = 0
          }
        }
      }

      const errorPatterns = {
        consecutiveErrors: currentConsecutive,
        maxConsecutiveErrors,
      }

      return {
        regionTimes,
        timeSinceLast,
        idleStats: {
          latestIdle,
          longestIdle,
        },
        frequency,
        terminalMetrics,
        codeMetrics,
        chatMetrics,
        tabVisibility,
        taskTestInteractions,
        testResults,
        errorPatterns,
        periodDuration,
      }
    }

    return {
      before: calculateStats(beforeFirstQuery),
      after: calculateStats(afterFirstQuery),
    }
  }, [beforeFirstQuery, afterFirstQuery])

  const currentStats = activeSubTab === 'before' ? observableStats.before : observableStats.after
  const currentRegionTimes = currentStats.regionTimes
  const totalTime = Object.values(currentRegionTimes).reduce((a, b) => a + b, 0)

  // Region display config
  const regionConfig = {
    CODE_EDITOR: { label: 'Code Editor', color: '#3b82f6' },
    CHAT_WINDOW: { label: 'Chat', color: '#8b5cf6' },
    TERMINAL: { label: 'Terminal', color: '#10b981' },
    TASK_DESCRIPTION: { label: 'Task', color: '#f59e0b' },
    TEST_CASES: { label: 'Tests', color: '#ef4444' },
    BACKGROUND: { label: 'Other', color: '#6b7280' },
  }

  // Frequency display config - grouped by category
  const frequencyGroups = {
    code: {
      label: 'Code Editor',
      color: '#3b82f6',
      items: {
        codeEdit: { label: 'Edits (type/indent)' },
        codeSelect: { label: 'Selections' },
        codeCopy: { label: 'Copies' },
        codePaste: { label: 'Pastes' },
        codeDelete: { label: 'Deletes' },
        codeUndo: { label: 'Undo/Redo' },
      },
    },
    terminal: {
      label: 'Terminal',
      color: '#10b981',
      items: {
        terminalRun: { label: 'Runs' },
        terminalError: { label: 'Errors' },
        terminalOutput: { label: 'Print Outputs' },
        terminalSelect: { label: 'Selections' },
        terminalCopy: { label: 'Copies' },
      },
    },
    chat: {
      label: 'Chat',
      color: '#8b5cf6',
      items: {
        chatInputType: { label: 'Input Typing' },
        chatInputSelect: { label: 'Input Selections' },
        chatInputCopy: { label: 'Input Copies' },
        chatInputPaste: { label: 'Input Pastes' },
        chatQuery: { label: 'Queries Sent' },
        chatHistorySelect: { label: 'History Selections' },
        chatHistoryCopy: { label: 'History Copies' },
      },
    },
    mouse: {
      label: 'Mouse',
      color: '#6b7280',
      items: {
        mouseMove: { label: 'Moves' },
        mouseClick: { label: 'Clicks' },
      },
    },
  }

  return (
    <div style={{
      backgroundColor: '#ffffff',
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    }}>
      {/* Main Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #e5e7eb',
      }}>
        <button
          onClick={() => setActiveTab('events')}
          style={{
            flex: 1,
            padding: '12px 16px',
            border: 'none',
            background: activeTab === 'events' ? '#ffffff' : '#f9fafb',
            borderBottom: activeTab === 'events' ? '2px solid #3b82f6' : '2px solid transparent',
            fontWeight: activeTab === 'events' ? 600 : 400,
            fontSize: '13px',
            color: activeTab === 'events' ? '#1f2937' : '#6b7280',
            cursor: 'pointer',
          }}
        >
          Events
        </button>
        <button
          onClick={() => setActiveTab('observables')}
          style={{
            flex: 1,
            padding: '12px 16px',
            border: 'none',
            background: activeTab === 'observables' ? '#ffffff' : '#f9fafb',
            borderBottom: activeTab === 'observables' ? '2px solid #3b82f6' : '2px solid transparent',
            fontWeight: activeTab === 'observables' ? 600 : 400,
            fontSize: '13px',
            color: activeTab === 'observables' ? '#1f2937' : '#6b7280',
            cursor: 'pointer',
          }}
        >
          Observables
        </button>
      </div>

      {/* Sub Tabs for Observables */}
      {activeTab === 'observables' && (
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
        }}>
          <button
            onClick={() => setActiveSubTab('before')}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: 'none',
              background: activeSubTab === 'before' ? '#ffffff' : 'transparent',
              borderBottom: activeSubTab === 'before' ? '2px solid #f59e0b' : '2px solid transparent',
              fontWeight: activeSubTab === 'before' ? 600 : 400,
              fontSize: '11px',
              color: activeSubTab === 'before' ? '#92400e' : '#6b7280',
              cursor: 'pointer',
            }}
          >
            Before Query
          </button>
          <button
            onClick={() => setActiveSubTab('after')}
            style={{
              flex: 1,
              padding: '8px 12px',
              border: 'none',
              background: activeSubTab === 'after' ? '#ffffff' : 'transparent',
              borderBottom: activeSubTab === 'after' ? '2px solid #f59e0b' : '2px solid transparent',
              fontWeight: activeSubTab === 'after' ? 600 : 400,
              fontSize: '11px',
              color: activeSubTab === 'after' ? '#92400e' : '#6b7280',
              cursor: 'pointer',
            }}
          >
            After Query
          </button>
        </div>
      )}

      {/* Event Log Tab */}
      {activeTab === 'events' && (
        <div 
          ref={containerRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '8px',
          }}
        >
          {events.map((event, index) => {
            const eventTime = event.timestamp - startTime
            const isActive = index === activeIndex
            const isPast = index < activeIndex

            return (
              <div
                key={index}
                ref={isActive ? activeRef : null}
                style={{
                  padding: '8px 12px',
                  marginBottom: '4px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  backgroundColor: isActive ? '#fef08a' : isPast ? '#f9fafb' : '#ffffff',
                  border: isActive ? '1px solid #eab308' : '1px solid #e5e7eb',
                  transition: 'background-color 0.2s',
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '4px',
                }}>
                  <span style={{
                    fontWeight: 600,
                    color: isActive ? '#854d0e' : '#374151',
                    fontSize: '11px',
                  }}>
                    {event.type}
                  </span>
                  <span style={{
                    color: '#9ca3af',
                    fontSize: '10px',
                  }}>
                    {formatTime(eventTime)}
                  </span>
                </div>
                <div style={{
                  color: '#6b7280',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {formatPayload(event)}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Observables Tab */}
      {activeTab === 'observables' && (
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px',
        }}>
          {/* 1. Time by Region */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
            }}>
              Time by Region
            </div>
            
            {/* Progress bar visualization */}
            {totalTime > 0 && (
              <div style={{
                display: 'flex',
                height: '8px',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px',
              }}>
                {Object.entries(currentRegionTimes).map(([region, time]) => {
                  if (time === 0) return null
                  const percentage = (time / totalTime) * 100
                  return (
                    <div
                      key={region}
                      style={{
                        width: `${percentage}%`,
                        backgroundColor: regionConfig[region]?.color || '#6b7280',
                      }}
                      title={`${regionConfig[region]?.label}: ${formatDuration(time)}`}
                    />
                  )
                })}
              </div>
            )}

            {/* Region breakdown */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {Object.entries(currentRegionTimes).map(([region, time]) => {
                const percentage = totalTime > 0 ? ((time / totalTime) * 100).toFixed(0) : 0
                return (
                  <div
                    key={region}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      fontSize: '11px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '2px',
                        backgroundColor: regionConfig[region]?.color || '#6b7280',
                      }} />
                      <span style={{ color: '#374151' }}>
                        {regionConfig[region]?.label || region}
                      </span>
                    </div>
                    <span style={{ color: '#6b7280' }}>
                      {formatDuration(time)} ({percentage}%)
                    </span>
                  </div>
                )
              })}
            </div>

            <div style={{
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '11px',
              fontWeight: 600,
            }}>
              <span style={{ color: '#374151' }}>Total</span>
              <span style={{ color: '#374151' }}>{formatDuration(totalTime)}</span>
            </div>
          </div>

          {/* 2. Event Frequency */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
            }}>
              Event Frequency
            </div>

            {Object.entries(frequencyGroups).map(([groupKey, group]) => (
              <div key={groupKey} style={{ marginBottom: '10px' }}>
                {/* Group Header */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '4px',
                  paddingBottom: '4px',
                  borderBottom: '1px solid #e5e7eb',
                }}>
                  <div style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '2px',
                    backgroundColor: group.color,
                  }} />
                  <span style={{ 
                    fontSize: '11px', 
                    fontWeight: 600, 
                    color: '#374151' 
                  }}>
                    {group.label}
                  </span>
                </div>

                {/* Group Items */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '16px' }}>
                  {Object.entries(group.items).map(([itemKey, item]) => {
                    const data = currentStats.frequency[itemKey]
                    return (
                      <div
                        key={itemKey}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '10px',
                        }}
                      >
                        <span style={{ color: '#6b7280' }}>
                          {item.label}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          {data.count} ({data.perMin}/min)
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}

            <div style={{
              marginTop: '4px',
              paddingTop: '8px',
              borderTop: '1px solid #e5e7eb',
              fontSize: '10px',
              color: '#9ca3af',
            }}>
              Period: {formatDuration(currentStats.periodDuration)}
            </div>
          </div>

          {/* 3. Time Since Last Events */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
            }}>
              Time Since Last Event
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#3b82f6',
                  }} />
                  <span style={{ color: '#374151' }}>Code Edit</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.timeSinceLast.codeEdit !== null 
                    ? formatDuration(currentStats.timeSinceLast.codeEdit)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#10b981',
                  }} />
                  <span style={{ color: '#374151' }}>Terminal Run</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.timeSinceLast.terminalRun !== null 
                    ? formatDuration(currentStats.timeSinceLast.terminalRun)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#ef4444',
                  }} />
                  <span style={{ color: '#374151' }}>Error</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.timeSinceLast.error !== null 
                    ? formatDuration(currentStats.timeSinceLast.error)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#8b5cf6',
                  }} />
                  <span style={{ color: '#374151' }}>Chat</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.timeSinceLast.chat !== null 
                    ? formatDuration(currentStats.timeSinceLast.chat)
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 4. Idle Time */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
            }}>
              Idle Time (gaps &gt; 2s)
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#6b7280',
                  }} />
                  <span style={{ color: '#374151' }}>Latest Idle</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.idleStats.latestIdle !== null 
                    ? formatDuration(currentStats.idleStats.latestIdle)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '2px',
                    backgroundColor: '#f59e0b',
                  }} />
                  <span style={{ color: '#374151' }}>Longest Idle</span>
                </div>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.idleStats.longestIdle !== null 
                    ? formatDuration(currentStats.idleStats.longestIdle)
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 5. Code Metrics */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#3b82f6',
              }} />
              Code Metrics
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Net Code Growth</span>
                <span style={{ 
                  color: currentStats.codeMetrics.netCodeGrowth >= 0 ? '#10b981' : '#ef4444',
                  fontWeight: 500,
                }}>
                  {currentStats.codeMetrics.netCodeGrowth >= 0 ? '+' : ''}
                  {currentStats.codeMetrics.netCodeGrowth} chars
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Delete:Type Ratio</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.codeMetrics.deletionToTypeRatio !== null 
                    ? currentStats.codeMetrics.deletionToTypeRatio
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 6. Terminal Metrics */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#10b981',
              }} />
              Terminal Metrics
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Mean Time Between Runs</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.terminalMetrics.meanTimeBetweenRuns !== null 
                    ? formatDuration(currentStats.terminalMetrics.meanTimeBetweenRuns)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Error → Edit Latency</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.terminalMetrics.errorToEditLatency !== null 
                    ? formatDuration(currentStats.terminalMetrics.errorToEditLatency)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Error Reading Time</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.terminalMetrics.errorReadingTime !== null 
                    ? formatDuration(currentStats.terminalMetrics.errorReadingTime)
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 7. Error Patterns */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#ef4444',
              }} />
              Error Patterns
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Current Consecutive Errors</span>
                <span style={{ 
                  color: currentStats.errorPatterns.consecutiveErrors >= 3 ? '#ef4444' : '#6b7280',
                  fontWeight: currentStats.errorPatterns.consecutiveErrors >= 3 ? 600 : 400,
                }}>
                  {currentStats.errorPatterns.consecutiveErrors}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Max Consecutive Errors</span>
                <span style={{ 
                  color: currentStats.errorPatterns.maxConsecutiveErrors >= 3 ? '#ef4444' : '#6b7280',
                  fontWeight: currentStats.errorPatterns.maxConsecutiveErrors >= 3 ? 600 : 400,
                }}>
                  {currentStats.errorPatterns.maxConsecutiveErrors}
                </span>
              </div>

              {currentStats.errorPatterns.maxConsecutiveErrors >= 3 && (
                <div style={{
                  fontSize: '10px',
                  color: '#ef4444',
                  backgroundColor: '#fef2f2',
                  padding: '6px 8px',
                  borderRadius: '4px',
                  marginTop: '4px',
                }}>
                  ⚠️ Student may be stuck - multiple errors without progress
                </div>
              )}
            </div>
          </div>

          {/* 8. Chat Metrics */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#8b5cf6',
              }} />
              Chat Metrics
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Time to Help (Error → Query)</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.chatMetrics.timeToHelp !== null 
                    ? formatDuration(currentStats.chatMetrics.timeToHelp)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Chat → Code Latency</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.chatMetrics.chatToCodeLatency !== null 
                    ? formatDuration(currentStats.chatMetrics.chatToCodeLatency)
                    : '—'}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Avg Query Length</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.chatMetrics.avgQueryLength !== null 
                    ? `${currentStats.chatMetrics.avgQueryLength} chars`
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 9. Task/Test Interactions */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#f59e0b',
              }} />
              Task/Test Interactions
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Task Selections</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.taskTestInteractions.taskSelect}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Task Copies</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.taskTestInteractions.taskCopy}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Test Selections</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.taskTestInteractions.testSelect}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Test Copies</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.taskTestInteractions.testCopy}
                </span>
              </div>
            </div>
          </div>

          {/* 10. Test Results */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            marginBottom: '8px',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#10b981',
              }} />
              Test Results
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Runs with Passes</span>
                <span style={{ color: '#10b981', fontWeight: 500 }}>
                  {currentStats.testResults.passCount}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Runs with Failures</span>
                <span style={{ color: '#ef4444', fontWeight: 500 }}>
                  {currentStats.testResults.failCount}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Last Result</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.testResults.lastPassedCount !== null 
                    ? `${currentStats.testResults.lastPassedCount}/${currentStats.testResults.lastTotalTests} passed`
                    : '—'}
                </span>
              </div>
            </div>
          </div>

          {/* 11. Tab Visibility */}
          <div style={{
            padding: '12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
          }}>
            <div style={{
              fontSize: '12px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}>
              <div style={{
                width: '10px',
                height: '10px',
                borderRadius: '2px',
                backgroundColor: '#f59e0b',
              }} />
              Tab Visibility (Distraction)
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Time Away</span>
                <span style={{ 
                  color: currentStats.tabVisibility.hiddenTime > 10000 ? '#ef4444' : '#6b7280',
                  fontWeight: currentStats.tabVisibility.hiddenTime > 10000 ? 500 : 400,
                }}>
                  {formatDuration(currentStats.tabVisibility.hiddenTime)}
                </span>
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '11px',
              }}>
                <span style={{ color: '#374151' }}>Times Switched Away</span>
                <span style={{ color: '#6b7280' }}>
                  {currentStats.tabVisibility.hiddenCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper to format payload for display
function formatPayload(event) {
  const { type, payload } = event

  switch (type) {
    // SESSION
    case 'SESSION_START':
      return `${payload?.window_width}x${payload?.window_height}`

    // GLOBAL
    case 'MOUSE_MOVE':
      return `${payload?.region} (${payload?.x?.toFixed(2)}, ${payload?.y?.toFixed(2)})`
    case 'MOUSE_CLICK':
      return `${payload?.region} (${payload?.x?.toFixed(2)}, ${payload?.y?.toFixed(2)})`
    case 'TAB_STATE':
      return payload?.visible ? 'Tab visible' : 'Tab hidden'
    case 'WINDOW_RESIZE':
      return `${payload?.width}x${payload?.height}`
    case 'PANEL_RESIZE':
      return `${payload?.panel}: ${payload?.newSize}px`

    // PREVIEW
    case 'PREVIEW_CLICK':
      return payload?.target?.id ? `#${payload.target.id}` : (payload?.target?.tag || '...')
    case 'PREVIEW_INPUT':
      return payload?.target?.id ? `#${payload.target.id}` : (payload?.target?.tag || '...')
    case 'PREVIEW_SCROLL':
      return `scrollY: ${payload?.scrollY}`
    case 'PREVIEW_SUBMIT':
      return payload?.target?.id ? `form#${payload.target.id}` : 'form submitted'
    case 'PREVIEW_FOCUS':
      return `${payload?.focused ? 'focused' : 'blurred'} ${payload?.target?.id ? '#' + payload.target.id : payload?.target?.tag || ''}`
    case 'PREVIEW_CONSOLE':
      return `[${payload?.level}] ${payload?.message?.slice(0, 30) || '...'}`
    case 'PREVIEW_SNAPSHOT':
      return `${payload?.trigger === 'manual' ? 'manual, ' : ''}${payload?.viewport?.width}x${payload?.viewport?.height}${payload?.screenshot ? '' : ' (no screenshot)'}`
    case 'PREVIEW_VIEWPORT_CHANGE':
      return `${payload?.label}${payload?.width ? ` (${payload.width}px)` : ''}`

    // TASK
    case 'TASK_SELECT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'TASK_COPY':
      return payload?.selected_text?.slice(0, 30) || '...'

    // TEST
    case 'TEST_SELECT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'TEST_COPY':
      return payload?.selected_text?.slice(0, 30) || '...'

    // CODE
    case 'CODE_TYPE':
      const typeText = payload?.changes?.[0]?.text || ''
      if (typeText === '\n' || typeText.startsWith('\n')) return '↵ enter'
      return typeText.slice(0, 30) || '...'
    case 'CODE_INDENT':
      return '→ tab'
    case 'CODE_SELECT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CODE_DELETE_SELECTION':
      return `deleted selection at ${payload?.changes?.[0]?.from}`
    case 'CODE_COPY':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CODE_PASTE':
      return payload?.changes?.[0]?.text?.slice(0, 30) || '...'
    case 'CODE_DELETE':
      return `deleted at ${payload?.changes?.[0]?.from}`
    case 'CODE_CUT':
      return `cut at ${payload?.changes?.[0]?.from}`
    case 'CODE_UNDO':
      return 'Undo'
    case 'CODE_REDO':
      return 'Redo'
    case 'CODE_UNKNOWN':
      return payload?.raw_action || 'Unknown action'
    case 'CODE_TAB_SWITCH':
      return `${payload?.from} → ${payload?.to}`

    // TERMINAL
    case 'TERMINAL_RUN':
      return `${payload?.code_length} chars`
    case 'TERMINAL_OUTPUT':
      return payload?.output?.slice(0, 30) || '...'
    case 'TEST_CASE_RESULT':
      return `${payload?.passed_count}/${payload?.total_tests} passed`
    case 'TERMINAL_ERROR':
      return `${payload?.type}: ${payload?.message?.slice(0, 20) || '...'}`
    case 'TERMINAL_SELECT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'TERMINAL_COPY':
      return payload?.selected_text?.slice(0, 30) || '...'

    // CHAT
    case 'CHAT_TYPE':
      return payload?.text?.slice(0, 30) || '...'
    case 'CHAT_SELECT_INPUT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CHAT_SELECT_HISTORY':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CHAT_COPY_INPUT':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CHAT_COPY_HISTORY':
      return payload?.selected_text?.slice(0, 30) || '...'
    case 'CHAT_PASTE':
      return payload?.pasted_text?.slice(0, 30) || '...'
    case 'CHAT_DELETE':
      return `"${payload?.text_before?.slice(0, 15) || ''}" → "${payload?.text_after?.slice(0, 15) || ''}"`
    case 'CHAT_QUERY':
      return payload?.text?.slice(0, 30) || '...'
    case 'CHAT_RESPONSE':
      return payload?.content?.slice(0, 30) || '...'
    case 'CHAT_ERROR':
      return payload?.error?.slice(0, 30) || 'Error'
    case 'CHAT_RESPONSE_READ':
      return `${Math.round((payload?.reading_time_ms || 0) / 1000)}s`
    case 'CHAT_ATTACHMENT':
      return `${payload?.attachment_type} (${payload?.frame_count} frame${payload?.frame_count === 1 ? '' : 's'})`

    default:
      return JSON.stringify(payload)?.slice(0, 30) || '...'
  }
}